<template>
    <div class="assistent-instruction-form">
        <flex-container
            size="full"
            height="full"
        >
            <flex-container
                size="full"
                direction="column"
                :gap="null"
            >
                <flex-container
                    align-items="center"
                    align-content="center"
                    justify-items="center"
                >
                    <text-decorator :size="FontSize.lg" bold>
                        Instructie
                    </text-decorator>
                    <chip-informative :color="'primary'">
                        Expert
                    </chip-informative>
                </flex-container>
                <text-description>Pas de prompt aan om het gedrag en de antwoorden van de AI Agent volledig af te stemmen op jouw specifieke behoeften.</text-description>
            </flex-container>
            <alert-block fill :color="'orange-500'">
                <template #prefix>
                    <svg-icon :icon="WarningIcon" />
                </template>
                <template #title>
                    {{ warningReadOnly }}
                    Let op: zodra je aanpassingen doorvoert, ben je zelf volledig verantwoordelijk voor de gevolgen van die gewijzigde instucties.
                    Vanaf dat moment zul je ook geen AI instructie-updates meer ontvangen vanuit het {{ brandLabel }}-team
                </template>
            </alert-block>

            <form-validation>
                <flex-container
                    justify-content="flex-end"
                    align-items="center"
                    :gap="null"
                >
                    <flex-container size="auto" align-items="center">
                        <text-decorator :size="FontSize.xs" weight="medium">
                            Zelfgemaakte instructies toedienen:
                        </text-decorator>
                        <flex-container size="auto">
                            <input-group
                                v-model="assistent.behavior.customInstructionEnabled"
                                inline
                            >
                                <template #input>
                                    <switch-input />
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>
                    <text-decorator :size="FontSize.xs">
                        {{ assistent.behavior.customInstructionEnabled ? 'Ingeschakeld' : 'Uitgeschakeld' }}
                    </text-decorator>
                </flex-container>
                <flex-container>
                    <input-group
                        v-model="instruction"
                    >
                        <template #input>
                            <text-area-input
                                :disabled="isReadonly"
                                height="70vh"
                            />
                        </template>
                    </input-group>
                </flex-container>
            </form-validation>
            <flex-container justify-content="flex-end">
                <button-block
                    v-if="instruction === assistent.behavior.customInstruction"
                    without-loading
                    @click="confirmReset?.toggleOverlay()"
                >
                    Herstel instructie
                </button-block>
                <button-block
                    v-if="assistent.behavior.customInstruction === null"
                    without-loading
                    @click="confirmChange?.toggleOverlay()"
                >
                    Wijzigen
                </button-block>
            </flex-container>
        </flex-container>
    </div>

    <confirmation-dialog
        ref="confirmReset"
        description="Bevestig dat je de instructies wil herstellen!"
        label="Bevestig reset"
    >
        <template #default>
            <text-heading>Weet u dit zeker?</text-heading>
            <p>
                Let op: Het herstellen van de instructies zal de aangepaste versie overschrijven.
                Deze actie kan niet ongedaan worden gemaakt en de wijzigingen gaan verloren.
                Controleer voordat je doorgaat!
            </p>
        </template>
        <template #actions="{ bindings }">
            <button-outline @click="bindings.overlay.closeOverlay();">
                Annuleren
            </button-outline>
            <button-block
                without-loading
                @click="() => {
                    bindings.overlay.closeOverlay();
                    resetInstruction();
                }"
            >
                Bevestig
            </button-block>
        </template>
    </confirmation-dialog>

    <confirmation-dialog
        ref="confirmChange"
        description="Bevestig dat je de instructies wil veranderen!"
        label="Bevestig verandering"
    >
        <template #default>
            <text-heading>Weet u dit zeker?</text-heading>
            <p>
                Let op: Het aanpassen van instructies kan de antwoorden beïnvloeden en ongewenste reacties richting de klant opleveren.
                Controleer wijzigingen zorgvuldig om negatieve gevolgen te voorkomen.
            </p>
        </template>
        <template #actions="{ bindings }">
            <button-outline @click="bindings.overlay.closeOverlay();">
                Annuleren
            </button-outline>
            <button-block
                without-loading
                @click="() => {
                    bindings.overlay.closeOverlay();
                    changeInstruction();
                }"
            >
                Bevestig
            </button-block>
        </template>
    </confirmation-dialog>
</template>

<script setup lang="ts">
    import {
        ChipInformative,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        FormValidation,
        InputGroup,
        SvgIcon,
        SwitchInput,
        TextAreaInput,
        TextDecorator,
        TextDescription,
    } from '@keyvue/one-ui';

    import type { Assistent } from '@keyvue/one-api-client';
    import { computed, onMounted, type Ref, ref, watch } from 'vue';
    import WarningIcon from '@keyvue/one-material-icons/icons/warning.svg?component';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    const isReadonly = ref(true);
    const { notifySaved } = useNotify();
    const confirmReset = ref<ExposedOverlay>();
    const confirmChange = ref<ExposedOverlay>();

    const { hasWinckBranding } = useAppBranding();
    const brandLabel = hasWinckBranding() ? 'Winck' : 'Heyloha';

    const warningReadOnly = computed(() => {
        if (isReadonly.value) {
            return 'Je staat op het punt de AI-instuctie aan te passen.';
        }
        return 'Je bent momenteel de AI-instructie aan het aanpassen.';
    });

    onMounted(() => {
        isReadonly.value = assistent.value.behavior.customInstruction === null;
    });

    const instruction = computed({
        get() {
            if (assistent.value.behavior.customInstruction !== null) {
                return assistent.value.behavior.customInstruction;
            }

            return assistent.value.instruction;
        },
        set(value: string) {
            assistent.value.behavior.customInstruction = value;
            emit('update:modelValue', assistent.value);
        },
    });

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    function changeInstruction() {
        isReadonly.value = false;
        assistent.value.behavior.customInstructionEnabled = true;
        if (assistent.value.behavior.customInstruction === null || assistent.value.behavior.customInstruction === 'null') {
            assistent.value.behavior.customInstruction = assistent.value.instruction;
        }
        emit('update:modelValue', assistent.value);
    }

    function resetInstruction() {
        assistent.value.behavior.customInstruction = null;
        assistent.value.behavior.customInstructionEnabled = false;
        isReadonly.value = true;
        emit('update:modelValue', assistent.value);
        notifySaved('Instructies hersteld!', 'Druk op opslaan om de herstelde instucties toe te passen!');
    }
</script>
